import { useEffect, useMemo, useState } from "react"

import { ContentObjectItem, VectorSearchQuery } from "@vertesia/common"
import { FilterX, X, FileCheck } from "lucide-react"
import { toast } from "sonner"

import { ActionTooltipButton } from "@/components/buttons/ActionTooltipButton"
import { DocumentDataTable } from "@/components/datatables/DocumentDataTable"
import { FiltersPanel } from "@/components/filters/FiltersPanel"
import { Button } from "@ui/button"
import { CONFIGURATION } from "@env/variables"
import { DOCUMENT_FACETS } from "@/constants/facets"
import { transformFacetStatusResponses, transformFacetTypeResponses, transformPropertiesFacetsWithFilter } from "@/lib/facets"
import { removeEmptyValues } from "@/lib/filters"
import { FacetResponse, PropertyFacet } from "@/models/facets"
import { useWatchDocumentSearchFacets, useWatchDocumentSearchResult } from "@/plugins/providers/document/DocumentSearchContext"
import { useUserSession } from "@session/UserSession"

interface SearchFilters {
    [key: string]: any
}

interface DocumentSelectionSearchPanelProps {
    information?: string
    children?: React.ReactNode
    multipleSelection?: ContentObjectItem[]
    onSelection?: (document: ContentObjectItem) => void
    onMultipleSelection?: (documents: ContentObjectItem[]) => void
}

export function DocumentSelectionSearchPanel({ information, children, multipleSelection, onSelection, onMultipleSelection }: Readonly<DocumentSelectionSearchPanelProps>) {
    const { hasMore, search, isSearching, error, objects } = useWatchDocumentSearchResult()
    const facets = useWatchDocumentSearchFacets()

    const { project, typeRegistry } = useUserSession()
    const documentTypes = useMemo(() => typeRegistry?.types || [], [typeRegistry?.types])
    const filteredDocumentTypes = useMemo(() => documentTypes.filter((type) => !CONFIGURATION.TYPES_TO_EXCLUDE.includes(type.id)), [documentTypes])

    const [filters, setFilters] = useState<SearchFilters>({})
    const [statuses, setStatuses] = useState<FacetResponse[]>([])
    const [properties, setProperties] = useState<PropertyFacet[]>([])
    const [types, setTypes] = useState<FacetResponse[]>([])
    const [users, setUsers] = useState<FacetResponse[]>([])
    const [hasSearched, setHasSearched] = useState(false)

    const [isLoadingMore, setIsLoadingMore] = useState(false)

    const [selectionDocuments, setSelectionDocuments] = useState<ContentObjectItem[]>(multipleSelection ?? [])

    const [clearFilters, setClearFilters] = useState(0)

    const searchObjects = async () => {
        const full_text = filters?.properties?.full_text
        const vectorText = filters?.vectorText as string
        const vector: VectorSearchQuery | undefined = vectorText ? {text: vectorText, config: {text: true, image: true, properties: true}} : undefined
        
        const properties = removeEmptyValues(filters?.properties)

        const query: any = filters ? {
            createdFrom: filters?.date?.createdFrom,
            createdTo: filters?.date?.createdTo,
            updatedFrom: filters?.date?.updatedFrom,
            updatedTo: filters?.date?.updatedTo,
            full_text,
            name: filters?.name,
            status: filters?.status,
            types: filters?.type ? [filters.type] : filteredDocumentTypes.map((type) => type.id),
            vector,
            match: {
                created_by: filters?.user,
                ...properties
            }
        } : {}

        search.query = query
        setHasSearched(true)
        await search.search()
    }

    const loadMoreDocuments = async () => {
        if (isLoadingMore || !hasMore) {
            return
        }

        setIsLoadingMore(true)

        try {
            await search.loadMore()
        } catch (error) {
            console.error("Error loading more documents:", error)
        } finally {
            setIsLoadingMore(false)
        }
    }

    const handleOnClearFilters = () => {
        setClearFilters(clearFilters + 1)
    }

    useEffect(() => {
        if (!error) {
            return
        }

        toast.error("Error while searching documents", {
            description: error.message,
            duration: 5000
        })
    }, [error])

    useEffect(() => {
        setTypes(transformFacetTypeResponses(facets.type, typeRegistry))
        setStatuses(transformFacetStatusResponses(facets.status))
        setUsers(transformFacetStatusResponses((facets as any).created_by))
        setProperties(transformPropertiesFacetsWithFilter(facets, DOCUMENT_FACETS(project?.id || "")))
    }, [facets])

    useEffect(() => {
        searchObjects()
    }, [])

    const handleOnSelection = (document: ContentObjectItem) => {
        if (onSelection) {
            onSelection(document)
        }
        if (onMultipleSelection) {
            const isAlreadySelected = selectionDocuments.some(doc => doc.id === document.id)

            if (isAlreadySelected) {
                setSelectionDocuments(selectionDocuments.filter(doc => doc.id !== document.id))
            } else {
                setSelectionDocuments([...selectionDocuments, document])
            }
        }
    }

    const confirmMultipleSelection = () => {
        if (onMultipleSelection) {
            onMultipleSelection(selectionDocuments)
        }
    }

    return (
        <div className="flex h-full w-full">
            <FiltersPanel properties={properties} statuses={statuses} types={types} users={users} onFiltersChange={setFilters} onSearch={() => searchObjects()} onClearFilters={clearFilters} />
            <div className='w-full h-full flex flex-col gap-2'>
                <div className='w-full flex flex-row gap-2 items-center justify-end p-2 pb-0'>
                    {
                        information &&
                        <div className='text-sm text-muted-foreground italic flex-1'>
                            {information}
                        </div>
                    }
                    <div className='flex h-10 gap-2'>
                        {
                            (onMultipleSelection && selectionDocuments.length !== 0) &&
                            <div className='flex items-center gap-1'>
                                <span className='text-sm text-gray-500'>{selectionDocuments.length} document{selectionDocuments.length > 1 ? "s" : ""} selected</span>
                                <ActionTooltipButton icon={<X />} tooltip='Clear Selection' variant='ghost' className='h-full text-gray-500' onClick={() => setSelectionDocuments([])} />
                                <Button variant='outline' onClick={confirmMultipleSelection}>
                                    <FileCheck />
                                    Confirm Selection
                                </Button>
                            </div>
                        }
                        {
                            filters && Object.keys(filters).length > 0 &&
                            <ActionTooltipButton icon={<FilterX />} tooltip='Clear Filters' variant='outline' className='h-full' onClick={handleOnClearFilters} />
                        }
                        {children}
                    </div>
                </div>
                <div className="flex-grow w-full overflow-auto px-2">
                    <DocumentDataTable
                        data={objects}
                        loading={isSearching}
                        loadingMore={isLoadingMore}
                        hasMore={hasMore}
                        loadMore={loadMoreDocuments}
                        onSelection={handleOnSelection}
                        selections={selectionDocuments}
                        hasSearched={hasSearched}
                    />
                </div>
            </div>
        </div>
    )
}